import React, {Fragment, useContext, useEffect, useState} from 'react';
import {Formik} from 'formik';
import {schema} from "./support-ticket-form-validation";
import {Ticket, TicketType} from "../../../app/models/Ticket";
import {
    EuiBreadcrumbs,
    EuiButton,
    EuiDescribedFormGroup,
    EuiFieldText,
    EuiForm,
    EuiFormRow,
    EuiLink,
    EuiLoadingContent,
    EuiPageContent,
    EuiPageContentHeader,
    EuiPageContentHeaderSection,
    EuiPanel,
    EuiSelect,
    EuiSpacer,
    EuiText,
    EuiTextArea
} from "@elastic/eui";
import {useHistory, useLocation, useParams} from 'react-router-dom';
import * as _ from "lodash";
import {Store} from "../../../app/stores/store-root";
import OrdersErrorModal from "../../orders/form/modal-order-no-balance";
import {Order} from "../../../app/models/order";
import {observer} from 'mobx-react-lite';
import {v4 as uuidv4} from 'uuid';

type SupportTicketFormProps = {
    readOnly?: boolean
}

const useQuery = () => {
    return new URLSearchParams(useLocation().search);
}

const SupportTicketForm = ({readOnly}: SupportTicketFormProps) => {


    //state
    const {id} = useParams();
    const history = useHistory();
    const store = useContext(Store);
    const query = useQuery().get('readonly');
    const location = useLocation();
    const [ticket, setTicket] = useState<Ticket | undefined>(new Ticket());
    const [order, setOrder] = useState<Order | undefined>(undefined);
    const [editMode, setEditMode] = useState(false);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const setupNewSupportTicket = (id: string) => {
        setLoading(true)
        store.Orders.LoadOrderByReference(id).then((item: Order | undefined) => {
            console.log("Object found ", item);
            if (item) {
                setOrder(item);
                const newTicket = new Ticket();
                newTicket.Id = uuidv4();
                newTicket.Reference = item.Reference;
                newTicket.CreatedByName = `${store.Users.Name} ${store.Users.FamilyName}`;
                newTicket.CreatedByUserName = store.Users.UserName;
                newTicket.CreatedBySub = store.Users.Sub;
                newTicket.CreateAt = new Date();
                setTicket(newTicket);
            }

            setLoading(false);
        });
    }

    const setupEditSupportTicket = (id: string) => {
        setLoading(true);
        store.SupportTickets.LoadTicket(id).then((item: Ticket | undefined) => {
            setTicket(item);
        }).finally(() => {
            setLoading(false)
        })
    }

    //load behavioour
    useEffect(() => {

        if (location.pathname.includes('edit'))
            setEditMode(true);

        if (editMode) {
            setupEditSupportTicket(id)
        } else {
            setupNewSupportTicket(id);
        }
    }, [id, store.Orders, editMode])


    const onCancel = (values:any)=>{
        store.SupportTickets.CancelTicket(values).then(() => {
            console.log('Support ticket cancelled');
            history.push('/');
        }).catch(() => {
            store.Modal.closeModal();
            store.Modal.openModal(<OrdersErrorModal error={'Error cancelling support ticket'}/>);
        }).finally(() => {
            setSubmitting(false);
        })
    }
    //handlers
    const onFormSubmit = (values: any) => {
        console.log(' Support ticket submitted ', JSON.stringify(values));
        setSubmitting(true);
        const t = new Ticket(values);


        if (editMode) {
            store.SupportTickets.SaveTicket(values).then(() => {
                console.log('Support ticket saved');
                history.push('/');
            }).catch(() => {
                store.Modal.closeModal();
                store.Modal.openModal(<OrdersErrorModal error={'Error saving support ticket'}/>);
            }).finally(() => {
                setSubmitting(false);
            })
        } else {
            store.SupportTickets.OpenTicket(values).then(() => {
                console.log('Support ticket submitted');
                history.push('/');
            }).catch(() => {
                store.Modal.closeModal();
                store.Modal.openModal(<OrdersErrorModal error={'Error creating support ticket'}/>);
            }).finally(() => {
                setSubmitting(false);
            })
        }

    }

    //Form settings
    const breadcrumbs = [
        {
            text: 'Remittance App',
            href: '#',
            onClick: (e: any) => {
                e.preventDefault();
            },
            'data-test-subj': 'breadcrumbsAnimals',
        },
        {
            text: 'Support Tickets',
            href: '#',
            onClick: (e: any) => {
                e.preventDefault();
            },
        },

        {
            text: 'Create',
        },
    ];


    const EditBreadcrumbs = [
        {
            text: 'Remittance App',
            href: '#',
            onClick: (e: any) => {
                e.preventDefault();
            },
            'data-test-subj': 'breadcrumbsAnimals',
        },
        {
            text: 'Support Tickets',
            href: '#',
            onClick: (e: any) => {
                e.preventDefault();
            },
        },
        {
            text: 'Edit',
        },
    ];

    const ticketTypeOptions = [{
        value: TicketType.unspecified,
        text: 'Select ticket type'
    },
        {
            value: 0,
            text: 'Correction/Change'
        }
        , {
            value: 1,
            text: 'Cancel'
        }]

    return (
        <Fragment>
            <Formik enableReinitialize={true} onSubmit={onFormSubmit} validationSchema={schema} initialValues={ticket!}>
                {
                    (formik) => (
                        <Fragment>
                            {
                                (loading) ? <EuiLoadingContent lines={10}/> : <Fragment><EuiPageContent>
                                    <EuiBreadcrumbs
                                        breadcrumbs={(editMode) ? EditBreadcrumbs : breadcrumbs}
                                        truncate={false}
                                        aria-label="An example of EuiBreadcrumbs"
                                    />
                                    <EuiSpacer size="xs"/>
                                    <EuiPageContentHeader>
                                        <EuiPageContentHeaderSection>
                                            <EuiText>
                                                <h1>{(editMode) ? `Support Ticket for ${ticket?.Reference}` : `New Support Ticket for ${id}`}</h1>
                                                <p>
                                                    Lorem ipsum dolor sit amet consectetur adipisicing elit. Quos,
                                                    aspernatur. Perferendis, asperiores necessitatibus? Voluptate, quae
                                                    aperiam enim laboriosam magni sed dolor doloribus non molestias illo
                                                    unde? Facere sint doloribus aliquid!
                                                </p>
                                            </EuiText>
                                        </EuiPageContentHeaderSection>
                                        {(editMode) ? (<EuiPageContentHeaderSection>
                                            <EuiButton fill onClick={()=>onCancel(formik.values)}>Cancel this ticket</EuiButton>
                                        </EuiPageContentHeaderSection>) : ''}

                                    </EuiPageContentHeader>
                                </EuiPageContent>
                                    <EuiSpacer/>
                                    <EuiForm isInvalid={!formik.isValid && formik.submitCount > 0}
                                             error={_.values(formik.errors)}>
                                        <form onSubmit={formik.handleSubmit}>
                                            <EuiPanel>
                                                <EuiDescribedFormGroup title={<h3>Details</h3>}
                                                                       description={
                                                                           <Fragment>
                                                                               A single text field that can be used to
                                                                               display
                                                                               additional text. It
                                                                               can have{' '}
                                                                               <EuiLink href="http://www.elastic.co"
                                                                                        target="_blank">
                                                                                   links
                                                                               </EuiLink>{' '}
                                                                               or any other type of content.
                                                                           </Fragment>}
                                                >
                                                    <EuiFormRow label={'Order Reference'}>
                                                        <EuiFieldText
                                                            {...formik.getFieldProps('Reference')}
                                                            readOnly={true}></EuiFieldText>
                                                    </EuiFormRow>
                                                    <EuiFormRow label={'Issue Type'}>
                                                        <EuiSelect disabled={submitting || readOnly}
                                                                   options={ticketTypeOptions} {...formik.getFieldProps('TicketType')}
                                                        ></EuiSelect>
                                                    </EuiFormRow>
                                                    <EuiFormRow label={'Notes'}>
                                                        <EuiTextArea disabled={submitting || readOnly}
                                                                     placeholder={'Describe here how do you want the' +
                                                                     ' administrator to help you'} {...formik.getFieldProps('Note')}
                                                        ></EuiTextArea>
                                                    </EuiFormRow>
                                                </EuiDescribedFormGroup>
                                            </EuiPanel>
                                            <div className={'flex justify-end mt-8 space-x-4'}>
                                                <EuiButton isLoading={submitting} type='submit'>
                                                    Submit </EuiButton>

                                                <EuiButton isLoading={submitting} type={"button"} onClick={() => {
                                                    history.push('/');
                                                }}>
                                                    Cancel </EuiButton>
                                            </div>
                                        </form>
                                    </EuiForm></Fragment>

                            }

                        </Fragment>
                    )
                }
            </Formik>
        </Fragment>
    );
};

export default observer(SupportTicketForm);